<script setup lang="ts">
// A small colour picker: a saturation/brightness square, a hue strip and a hex field. The colour updates live as it's
// dragged. Built rather than using <input type="color">, whose picker looks different in every browser. Drawn for a
// dark background.
import { computed, ref, useTemplateRef, watch } from 'vue';
import { colorHexParse, colorHexToHsv, colorHsvToHex, type ColorHsv } from './color-picker';

// Uppercase #RRGGBB.
const color = defineModel<string>({ required: true });

// The picker works in HSV and keeps its own copy, so hue and saturation aren't lost when dragging through black, grey
// or white, where they can't be recovered from the hex.
const hsv = ref<ColorHsv>(colorHexToHsv(color.value));
watch(color, (hex) => {
  if (colorHsvToHex(hsv.value) !== hex) hsv.value = colorHexToHsv(hex);
});
function hsvSet(next: ColorHsv) {
  hsv.value = next;
  color.value = colorHsvToHex(next);
}

const clamp = (n: number) => Math.min(1, Math.max(0, n));
const hueColor = computed(() => colorHsvToHex({ h: hsv.value.h, s: 1, v: 1 }));

// Dragging follows the pointer even outside the control. Positions are fractions of the control's rendered size, so
// they're right even when it's drawn scaled.
type Track = 'area' | 'hue';
const dragging = ref<Track | null>(null);
function trackSet(track: Track, el: HTMLElement, event: PointerEvent) {
  const rect = el.getBoundingClientRect();
  const x = clamp((event.clientX - rect.left) / rect.width);
  const y = clamp((event.clientY - rect.top) / rect.height);
  if (track === 'area') hsvSet({ ...hsv.value, s: x, v: 1 - y });
  else hsvSet({ ...hsv.value, h: Math.min(x * 360, 359.9) });
}
function dragStart(track: Track, event: PointerEvent) {
  const el = event.currentTarget as HTMLElement;
  el.setPointerCapture(event.pointerId);
  el.focus();
  dragging.value = track;
  trackSet(track, el, event);
}
function dragMove(track: Track, event: PointerEvent) {
  if (dragging.value === track) trackSet(track, event.currentTarget as HTMLElement, event);
}

// Arrow keys nudge; with shift, they nudge further.
function areaKey(event: KeyboardEvent) {
  const step = event.shiftKey ? 0.1 : 0.02;
  const { s, v } = hsv.value;
  const moves: Record<string, Partial<ColorHsv>> = {
    ArrowLeft: { s: clamp(s - step) },
    ArrowRight: { s: clamp(s + step) },
    ArrowDown: { v: clamp(v - step) },
    ArrowUp: { v: clamp(v + step) },
  };
  if (!moves[event.key]) return;
  event.preventDefault();
  hsvSet({ ...hsv.value, ...moves[event.key] });
}
function hueKey(event: KeyboardEvent) {
  const step = event.shiftKey ? 10 : 2;
  const delta = { ArrowLeft: -step, ArrowDown: -step, ArrowRight: step, ArrowUp: step }[event.key];
  if (delta === undefined) return;
  event.preventDefault();
  hsvSet({ ...hsv.value, h: (hsv.value.h + delta + 360) % 360 });
}

// The hex field follows the colour, except while someone's typing in it. It applies as soon as it holds a valid
// colour, and goes back to the current colour if it's left invalid.
const hexText = ref(color.value);
const hexEl = useTemplateRef<HTMLInputElement>('hex');
watch(color, (hex) => {
  if (document.activeElement !== hexEl.value) hexText.value = hex;
});
function hexInput(text: string) {
  hexText.value = text;
  const hex = colorHexParse(text);
  if (hex && hex !== color.value) color.value = hex;
}

const areaEl = useTemplateRef<HTMLElement>('area');
defineExpose({ focus: () => areaEl.value?.focus({ preventScroll: true }) });
</script>

<template>
  <div class="cp">
    <div
      ref="area"
      class="cp-area"
      role="slider"
      tabindex="0"
      aria-label="Saturation and brightness"
      :aria-valuenow="Math.round(hsv.s * 100)"
      :aria-valuetext="`Saturation ${Math.round(hsv.s * 100)}%, brightness ${Math.round(hsv.v * 100)}%`"
      :style="{ background: hueColor }"
      @pointerdown="dragStart('area', $event)"
      @pointermove="dragMove('area', $event)"
      @pointerup="dragging = null"
      @pointercancel="dragging = null"
      @keydown="areaKey"
    >
      <span
        class="cp-thumb"
        :style="{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, background: color }"
      ></span>
    </div>
    <div
      class="cp-hue"
      role="slider"
      tabindex="0"
      aria-label="Hue"
      aria-valuemin="0"
      aria-valuemax="360"
      :aria-valuenow="Math.round(hsv.h)"
      @pointerdown="dragStart('hue', $event)"
      @pointermove="dragMove('hue', $event)"
      @pointerup="dragging = null"
      @pointercancel="dragging = null"
      @keydown="hueKey"
    >
      <span class="cp-thumb" :style="{ left: `${(hsv.h / 360) * 100}%`, top: '50%', background: hueColor }"></span>
    </div>
    <label class="cp-hex">
      <span class="cp-preview" :style="{ background: color }"></span>
      <input
        ref="hex"
        :value="hexText"
        type="text"
        spellcheck="false"
        autocomplete="off"
        maxlength="7"
        aria-label="Hex colour"
        @input="hexInput(($event.target as HTMLInputElement).value)"
        @blur="hexText = color"
      />
    </label>
  </div>
</template>

<style scoped>
.cp {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}
.cp-area,
.cp-hue {
  position: relative;
  touch-action: none;
  cursor: crosshair;
}
.cp-area {
  height: 132px;
  border-radius: 10px;
}
/* White from the left for saturation, black from the bottom for brightness, over the pure hue. */
.cp-area::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent);
}
.cp-hue {
  height: 14px;
  border-radius: 999px;
  background: linear-gradient(to right, #f00, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00);
}
.cp-thumb {
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2.5px solid #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
  transform: translate(-50%, -50%);
  pointer-events: none;
}
.cp-area:focus-visible,
.cp-hue:focus-visible {
  outline: 2px solid #ffc400;
  outline-offset: 3px;
}
.cp-hex {
  display: flex;
  align-items: center;
  gap: 10px;
}
.cp-preview {
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1.5px rgba(255, 255, 255, 0.3);
}
.cp-hex input {
  flex: 1;
  min-width: 0;
  background: transparent;
  color: #fff;
  border: 1px solid #2d475e;
  border-radius: 8px;
  padding: 6px 10px;
  font: inherit;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.cp-hex input:focus {
  outline: none;
  border-color: #ffc400;
}
</style>
